import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import {
  isInRange,
  monthIdString,
  timestampToDate,
  timeOfDayFromTimeZoneToLocal,
  getStartOf,
  isSameDay,
  timeOfDayFromLocalToTimeZone,
} from './dates';
import { DAY_PACKAGES, PER_HOUR, PER_MINUTE, DELIVERY_FEE, PICKUP_FEE, REQUIRED_UPFRONT } from '../../src/util/constants';

// RAS-2520: was a hand-written subset that silently omitted threeDaysPrice/fourDaysPrice/
// fiveDaysPrice, so a listing defaulting to one of those counted as neither day- nor
// time-based and fell through to duration inference. DAY_PACKAGES (constants.js) is the
// single canonical list and is what OrderPanel.js itself uses.
const DAY_BASED_PACKAGES = DAY_PACKAGES;
const TIME_BASED_PACKAGES = [PER_HOUR, PER_MINUTE];
const DAILY_PACKAGE_TYPE = 'daily';
const HOURLY_PACKAGE_TYPE = 'hourly';


const getListingPackageTypes = (listing) => {
  const packages = Object.keys(listing?.attributes?.publicData?.prices || {});
  const types = new Set();
  packages.forEach(pkg => {
    if (DAY_BASED_PACKAGES.includes(pkg)) {
      types.add(DAILY_PACKAGE_TYPE);
    } else if (TIME_BASED_PACKAGES.includes(pkg)) {
      types.add(HOURLY_PACKAGE_TYPE);
    }
  });
  return Array.from(types).sort();
};

/**
 * Find a cart item by ID from Redux cart state
 * @param {Object} cartState - Redux cart state with items structure: { [authorId]: [items] }
 * @param {String} cartItemId - ID of the cart item to find
 * @returns {Object|null} Cart item if found, null otherwise
 */
export const getCartItemById = (cartState, cartItemId) => {
  if (!cartState || !cartState.items || !cartItemId) {
    return null;
  }

  // Cart items are organized by authorId: { [authorId]: [items] }
  for (const authorId in cartState.items) {
    const items = cartState.items[authorId];
    if (Array.isArray(items)) {
      const item = items.find(i => i.id === cartItemId);
      if (item) {
        return item;
      }
    }
  }

  return null;
};

/**
 * Find the most recently added cart item for a given listing (any dates).
 * Used to pre-fill the OrderPanel when navigating back from checkout without a cartItemId URL param.
 * @param {Object} cartState - Redux cart state
 * @param {String} listingId - Listing UUID
 * @returns {Object|null} Most recent cart item or null
 */
export const getCartItemByListingId = (cartState, listingId) => {
  if (!cartState?.items || !listingId) return null;

  let mostRecent = null;
  for (const authorId in cartState.items) {
    const items = cartState.items[authorId];
    if (Array.isArray(items)) {
      items.forEach(item => {
        const id = item.listingId || item.listing?.id?.uuid || item.listing?.id;
        if (id === listingId) {
          if (!mostRecent || (item.addedAt || '') > (mostRecent.addedAt || '')) {
            mostRecent = item;
          }
        }
      });
    }
  }
  return mostRecent;
};

/**
 * Check if a date is contained within any timeslot for that day
 * For date-based bookings: checks if the entire day is covered by time slots
 * For time-based bookings: checks if the specific time is in a slot
 * @param {Array} timeSlots - Array of timeslot objects
 * @param {Date} date - Date to check
 * @param {String} timeZone - Timezone string
 * @param {String} packageType - Package type ('daily' or 'hourly')
 * @returns {Boolean} True if date is available
 */
const timeSlotsContain = (timeSlots, date, timeZone, packageType = DAILY_PACKAGE_TYPE) => {
  if (packageType === HOURLY_PACKAGE_TYPE) {
    // For hourly bookings, check if the specific time is in a slot
    const foundIndex = timeSlots.findIndex(slot =>
      isInRange(
        date,
        slot.attributes.start,
        slot.attributes.end,
        'hour',
        timeZone
      )
    );
    return foundIndex > -1;
  } else {
    // For daily bookings, check if the entire day is covered
    // A day is available if there's a time slot that starts at or before 00:00 of that day
    // and ends after 00:00 of that day
    const dateMoment = moment.tz(date, timeZone);
    const dayStart = dateMoment.startOf('day');

    return timeSlots.some(slot => {
      const slotStart = moment.tz(slot.attributes.start, timeZone).clone().startOf('day');
      const slotEnd = moment.tz(slot.attributes.end, timeZone).clone().startOf('day');

      // Check if the slot covers the start of the day
      // Slot must start at or before day start (00:00), and end after day start
      return slotStart.isSameOrBefore(dayStart) && slotEnd.isAfter(dayStart);
    });
  }
};

/**
 * Get timeslots for a specific month
 * @param {Object} monthlyTimeSlots - Monthly timeslots structure
 * @param {Date} date - Date to get month for
 * @param {String} timeZone - Timezone string
 * @returns {Array} Array of timeslots for that month
 */
const pickMonthlyTimeSlots = (monthlyTimeSlots, date, timeZone) => {
  const monthId = monthIdString(date, timeZone);
  return monthlyTimeSlots?.[monthId]?.timeSlots || [];
};

/**
 * Find first blocked date between two dates (recursive check)
 * This replicates the logic from BookingDatesForm to ensure consistency
 * @param {Object} monthlyTimeSlots - Monthly timeslots structure
 * @param {Moment} startDate - Start date (exclusive)
 * @param {Moment} endDate - End date (exclusive)
 * @param {String} timeZone - Timezone string
 * @param {String} packageType - Package type ('daily' or 'hourly')
 * @returns {Moment|null} First blocked date or null if all dates are available
 */
const firstBlockedBetween = (
  monthlyTimeSlots,
  startDate,
  endDate,
  timeZone,
  packageType = DAILY_PACKAGE_TYPE,
  offset = 0,
  offsetUnit = 'days'
) => {
  const firstDate = getStartOf(startDate, 'day', timeZone, offset, offsetUnit);
  if (isSameDay(firstDate, endDate, timeZone)) {
    return null; // All dates are available
  }

  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, firstDate, timeZone);
  const contains = timeSlotsContain(timeSlots, firstDate, timeZone, packageType);
  return contains
    ? firstBlockedBetween(monthlyTimeSlots, firstDate, endDate, timeZone, packageType, 1, offsetUnit)
    : firstDate; // Found a blocked date
};

/**
 * Validate that dates fall within available timeslots
 * For date-based bookings: ensures every day in the range is available
 * For time-based bookings: ensures the exact time range is within a timeslot
 * @param {Date} startDate - Booking start date
 * @param {Date} endDate - Booking end date
 * @param {Object} monthlyTimeSlots - Monthly timeslots structure
 * @param {String} timeZone - Timezone string
 * @param {String} selectedPackage - Package type (PER_DAY, PER_HOUR, etc.)
 * @returns {Boolean|null} True if dates are valid, false if invalid, null if cannot determine
 */
export const validateDatesAgainstTimeSlots = (startDate, endDate, monthlyTimeSlots, timeZone, packageType) => {

  if (!startDate || !endDate || !timeZone) {
    return false;
  }

  // If monthlyTimeSlots is not provided or empty, we cannot validate
  // Return null to indicate uncertainty (should not prefill)
  if (!monthlyTimeSlots || Object.keys(monthlyTimeSlots).length === 0) {
    return null; // Cannot determine - flag it
  }

  const isTimeBased = packageType === HOURLY_PACKAGE_TYPE;
  const isDateBased = packageType === DAILY_PACKAGE_TYPE;

  // Convert dates to moment objects in the listing's timezone
  // For date-based bookings, normalize to start of day (like isBlockedBetween does)
  // For time-based bookings, preserve the exact time
  let startMoment, endMoment;

  if (isDateBased) {
    // For date-based bookings: normalize to start of day in listing timezone
    // This matches the logic in BookingDatesForm.isBlockedBetween
    // Cart dates are stored in UTC, so we need to convert them properly
    const startInListingTZ = getStartOf(
      timeOfDayFromLocalToTimeZone(startDate, timeZone),
      'day',
      timeZone
    );
    const endInListingTZ = getStartOf(
      timeOfDayFromLocalToTimeZone(endDate, timeZone),
      'day',
      timeZone
    );
    startMoment = moment.tz(startInListingTZ, timeZone);
    endMoment = moment.tz(endInListingTZ, timeZone);
  } else {
    // For time-based bookings: preserve exact time
    startMoment = moment.tz(startDate, timeZone);
    endMoment = moment.tz(endDate, timeZone);
  }

  // Check if dates are in the past
  const now = moment.tz(timeZone);
  if (startMoment.isBefore(now, 'day') || endMoment.isBefore(now, 'day')) {
    return false;
  }

  // Check if start is before end
  if (!startMoment.isBefore(endMoment)) {
    return false;
  }

  if (isTimeBased) {
    // For time-based bookings: validate that the exact time range is within a timeslot
    // Hourly bookings are always single-day, so both times must be on the same day and in the same slot

    // Verify start and end are on the same day (hourly bookings cannot span multiple days)
    if (!startMoment.isSame(endMoment, 'day')) {
      return false;
    }

    // Get the month for the booking (start and end are same day, so same month)
    const monthId = monthIdString(startMoment.toDate(), timeZone);
    const monthSlots = monthlyTimeSlots[monthId];

    if (!monthSlots || !monthSlots.timeSlots || monthSlots.timeSlots.length === 0) {
      // No timeslots for this month - cannot validate time-based booking
      return null; // Cannot determine - flag it
    }

    // Check if both start and end times are within the same timeslot
    const isTimeRangeValid = monthSlots.timeSlots.some(slot =>
      isInRange(startMoment.toDate(), slot.attributes.start, slot.attributes.end, 'hour', timeZone) &&
      isInRange(endMoment.toDate(), slot.attributes.start, slot.attributes.end, 'hour', timeZone)
    );

    return isTimeRangeValid;
  } else if (isDateBased) {
    // For date-based bookings: use firstBlockedBetween to ensure every day is available
    // This replicates the exact validation logic used in BookingDatesForm
    // firstBlockedBetween expects Date objects, not moment objects
    const firstBlocked = firstBlockedBetween(
      monthlyTimeSlots,
      startMoment.toDate(),
      endMoment.toDate(),
      timeZone,
      packageType,
      0,
      'days'
    );

    // If firstBlocked is null, all dates are available
    // If firstBlocked is a date, that date is blocked
    return firstBlocked === null;
  }

  // Unknown package type - cannot validate
  return null; // Cannot determine - flag it
};


/**
 * Validate fees against listing's additionalFees configuration
 * Filters out invalid fees and returns validated fee objects
 * @param {Object} draft - Draft object with fees to validate
 * @param {Object} listing - Listing object with additionalFees configuration
 * @returns {Object} Validated fees object
 */
const validateFeesAgainstListing = (draft, listing) => {
  const { additionalFees = [] } = listing?.attributes?.publicData || {};

  // Build lookup map for efficient validation
  const feeTypeMap = new Map();
  additionalFees.forEach(fee => {
    feeTypeMap.set(fee.feeType, fee);
  });

  // Validate optional fees (whenToApply === 'requiredUpfrontOptional')
  // selectedFeeTypes is the current field; otherOptionalFees is the legacy fallback
  const feesToValidate = draft.selectedFeeTypes || draft.otherOptionalFees;
  const validatedOptionalFees = Array.isArray(feesToValidate)
    ? feesToValidate.filter(feeType => {
      const fee = feeTypeMap.get(feeType);
      return fee && fee.whenToApply === 'requiredUpfrontOptional';
    })
    : [];

  // Validate required fees (whenToApply === 'requiredUpfront')
  const validatedRequiredFees = Array.isArray(draft.requiredFees)
    ? draft.requiredFees.filter(feeType => {
      const fee = feeTypeMap.get(feeType);
      return fee && fee.whenToApply === 'requiredUpfront';
    })
    : [];

  // Validate usage-multiple fees (feeCalculationType === 'usageMultiple')
  const validatedDynamicFees = {};
  const usageMultipleValues = draft.usageMultipleValues || draft.dynamicFees;
  if (usageMultipleValues && typeof usageMultipleValues === 'object') {
    Object.keys(usageMultipleValues).forEach(feeType => {
      const fee = feeTypeMap.get(feeType);
      if (fee && fee.feeCalculationType === 'usageMultiple') {
        validatedDynamicFees[feeType] = usageMultipleValues[feeType];
      }
    });
  }

  // Check if delivery/pickup fees exist in listing
  const hasDeliveryFee = feeTypeMap.has('deliveryFee');
  const hasPickupFee = feeTypeMap.has('pickupFee');

  return {
    otherOptionalFees: validatedOptionalFees,
    requiredFees: validatedRequiredFees,
    dynamicFees: validatedDynamicFees,
    hasDeliveryFee,
    hasPickupFee,
  };
};

/**
 * Extract time components from bookingDates for time-based forms
 * @param {Object} bookingDates - Object with startDate and endDate
 * @param {String} timeZone - Timezone string
 * @returns {Object} Object with bookingStartTime and bookingEndTime as timestamps
 */
export const extractTimeFromBookingDates = (bookingDates, timeZone) => {
  if (!bookingDates || !bookingDates.startDate || !bookingDates.endDate || !timeZone) {
    return { bookingStartTime: null, bookingEndTime: null };
  }

  const { startDate, endDate } = bookingDates;

  // Convert dates to moment in listing timezone
  const startMoment = moment.tz(startDate, timeZone);
  const endMoment = moment.tz(endDate, timeZone);

  // Convert to timestamps (milliseconds)
  const bookingStartTime = startMoment.valueOf();
  const bookingEndTime = endMoment.valueOf();

  return { bookingStartTime, bookingEndTime };
};

/**
 * Extracts {startDate, endDate} (real Date objects) from a cart item/rental-default/
 * storefront draft, whichever of bookingDates or bookingStartTime+bookingEndTime it
 * carries. Returns null if the draft has no usable date/time range.
 */
export const getDraftBookingDateRange = draft => {
  const hasBookingDates = draft?.bookingDates?.startDate && draft?.bookingDates?.endDate;
  const hasBookingTimes = draft?.bookingStartTime && draft?.bookingEndTime;
  if (!hasBookingDates && !hasBookingTimes) {
    return null;
  }

  const rawStart = hasBookingDates
    ? draft.bookingDates.startDate
    : timestampToDate(draft.bookingStartTime);
  const rawEnd = hasBookingDates
    ? draft.bookingDates.endDate
    : timestampToDate(draft.bookingEndTime);
  const startDate = rawStart instanceof Date ? rawStart : new Date(rawStart);
  const endDate = rawEnd instanceof Date ? rawEnd : new Date(rawEnd);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return null;
  }

  return { startDate, endDate };
};

/**
 * Returns { monthId, start, end } for every calendar month a draft's booking range
 * touches (inclusive of both ends) - `start`/`end` are that month's own boundaries
 * (not the draft's), matching the {monthId, start, end} shape ListingPage.duck.js's
 * fetchTimeSlots expects (it keys the fetched slots by `monthIdString(start, ...)`,
 * so each month must be requested as its own start/end pair - a single call spanning
 * multiple months would file every returned slot under just the first month's id,
 * leaving the later months looking permanently unfetched).
 */
export const getRequiredMonthsForDraft = (draft, timeZone) => {
  if (!timeZone) {
    return [];
  }
  const range = getDraftBookingDateRange(draft);
  if (!range) {
    return [];
  }

  const months = [];
  let cursor = moment.tz(range.startDate, timeZone).startOf('month');
  const lastMonth = moment.tz(range.endDate, timeZone).startOf('month');
  while (cursor.isSameOrBefore(lastMonth, 'month')) {
    const start = cursor.toDate();
    const end = cursor.clone().add(1, 'month').toDate();
    months.push({ monthId: cursor.format('YYYY-MM'), start, end });
    cursor = cursor.clone().add(1, 'month');
  }
  return months;
};

/**
 * Back-compat convenience wrapper over getRequiredMonthsForDraft for callers that
 * only need the id strings (e.g. the monthlyTimeSlots presence check below).
 */
export const getRequiredMonthIdsForDraft = (draft, timeZone) =>
  getRequiredMonthsForDraft(draft, timeZone).map(m => m.monthId);

/**
 * The {start, end} pairs (one per calendar month, see getRequiredMonthsForDraft) that
 * monthlyTimeSlots doesn't have an entry for yet - ready to pass straight to
 * onFetchTimeSlots(listingId, start, end, timeZone), one call per pair.
 */
export const getMissingMonthRangesForDraft = (draft, monthlyTimeSlots, timeZone) =>
  getRequiredMonthsForDraft(draft, timeZone).filter(m => !monthlyTimeSlots?.[m.monthId]);

/**
 * True once monthlyTimeSlots has an entry for every month a draft's booking range
 * touches (see getRequiredMonthIdsForDraft) - regardless of whether that entry's
 * timeSlots array is empty or populated, since an empty array is a genuine "nothing
 * available" answer while a MISSING key means the fetch hasn't happened yet.
 *
 * OrderPanel.js's mount-time fetch only ever requests "today + 1 month", so a cart
 * item/rental-default/storefront draft booked further out than that touches a month
 * this hasn't fetched. Without this check, computeInitialValuesFromDraft's date-based
 * validation (firstBlockedBetween -> pickMonthlyTimeSlots) can't tell "not fetched
 * yet" apart from "fetched, and genuinely no slot covers this day" - both read back
 * as an empty timeSlots array - so it silently reports the prefill as invalid instead
 * of "not ready yet, retry later". Callers should use this as a precondition and skip
 * (without marking the item as processed) until it's true, exactly like the existing
 * `cartState.items === null` retry guard in OrderPanel.js.
 */
export const hasRequiredMonthlyTimeSlotsForDraft = (draft, monthlyTimeSlots, timeZone) => {
  const monthIds = getRequiredMonthIdsForDraft(draft, timeZone);
  if (monthIds.length === 0) {
    return true;
  }
  return monthIds.every(id => !!monthlyTimeSlots?.[id]);
};

/**
 * Compute initial values from a draft object (cart item or rental defaults)
 * Validates availability and handles all edge cases
 * @param {Object} draft - Draft object (cart item or rental defaults)
 * @param {Object} listing - Listing object
 * @param {Object} monthlyTimeSlots - Monthly timeslots structure
 * @param {String} timeZone - Timezone string
 * @returns {Object|null} Initial values object or null if invalid
 */
export const computeInitialValuesFromDraft = (draft, listing, monthlyTimeSlots, timeZone) => {
  const hasBookingDates = draft.bookingDates && draft.bookingDates.startDate && draft.bookingDates.endDate;
  const hasBookingTimes = draft.bookingStartTime && draft.bookingEndTime;
  if (!draft || !listing || (!hasBookingDates && !hasBookingTimes)) {
    return null;
  }

  // LOGIC FIX 1 (manual QA, mobile listing page): getListingPackageTypes below only
  // checks whether the listing has ANY package of the inferred type configured at
  // all - a listing with both a perDayPrice AND a perHourPrice tier would let a
  // day-based prefill through even though its defaultPriceKey (the tier it actually
  // opens to/displays) is hourly, showing the day calendar instead of the hourly
  // time picker. This is a stricter, additional check specifically against
  // defaultPriceKey, on top of (not instead of) the existing check below - applies
  // uniformly to every prefill source that funnels through this shared function
  // (cart item, storefront URL, rental defaults), without touching OrderPanel.js's
  // priority ladder order at all.
  //
  // Manual QA follow-up: this originally guarded only the day-prefill-onto-time-based-
  // listing direction. The opposite case turned out to be just as reachable - a
  // remembered hourly/minute range (rental defaults are stored globally, not per
  // listing) replayed onto a day-based listing - and had the same effect in reverse:
  // the listing opened the hourly time picker and hourly footer even though its
  // defaultPriceKey is day-based, i.e. a form it doesn't sell. The rule is now
  // symmetric: a draft's modality must match the listing's own defaultPriceKey
  // modality, in either direction, or it isn't a usable prefill for this listing.
  // (OrderPanel.js additionally clamps the rendered form to defaultPriceKey, so this
  // is defence in depth: this stops the bad values landing, that stops any that do
  // from switching the form.)
  //
  // RAS-2520 (booking category toggle): the reasoning above holds only while a listing
  // can be booked in exactly one modality. It no longer can - the toggle lets a renter
  // book a listing in any category it sells, so a listing whose defaultPriceKey is
  // perDayPrice can now legitimately have an HOURLY cart item. Clamping that item to
  // defaultPriceKey's modality silently reinterpreted it as a day booking whose range
  // happened to be under 24h, i.e. a 0-day range - which is how a 1-hour cart entry came
  // back looking like the listing's earlier 2-day one.
  //
  // A booking that already exists carries the tier it was actually made with, on its own
  // `package` field (cart.duck.js writes it on every add). That is ground truth about
  // what was booked and outranks the listing's *default*. Requiring the listing to still
  // sell that tier keeps a stale entry for a since-removed tier from slipping through.
  //
  // Drafts with no `package` - remembered rental defaults (stored globally, not per
  // listing) and storefront URL ranges - are unchanged: they still clamp to
  // defaultPriceKey, which is exactly the case the original fix above was written for.
  const listingDefaultPriceKey = listing?.attributes?.publicData?.defaultPriceKey;
  const listingPrices = listing?.attributes?.publicData?.prices || {};
  const draftPackage = draft.package;
  const draftPackageIsSold = !!draftPackage && !!listingPrices[draftPackage];
  const modalityKey = draftPackageIsSold ? draftPackage : listingDefaultPriceKey;

  const listingIsTimeBased = TIME_BASED_PACKAGES.includes(modalityKey);
  const listingIsDayBased = DAY_BASED_PACKAGES.includes(modalityKey);

  if (listingIsTimeBased && hasBookingDates && !hasBookingTimes) {
    // An hourly/minute booking stored as a bookingDates range (which is exactly how
    // BookingTimeForm serialises one into the cart - see its addToCartHandler) is a
    // legitimate time-based draft, not a day draft. Only reject when there is genuinely
    // no time information to recover, i.e. the range sits on whole-day boundaries.
    if (!draftPackageIsSold) {
      return null;
    }
  }
  if (listingIsDayBased && hasBookingTimes && !hasBookingDates) {
    return null;
  }

  const daysMaybe = hasBookingDates
    ? moment(draft.bookingDates.endDate).diff(moment(draft.bookingDates.startDate), 'days')
    : null;

  // Manual QA: this used to be inferred purely from the draft's own duration
  // (`daysMaybe >= 1 ? daily : hourly`), which silently reclassified any sub-day range
  // as an HOURLY booking - even on a listing whose defaultPriceKey is perDayPrice.
  // A stale/short cart entry on a day-based listing therefore came back as an hourly
  // prefill, which downstream became a minute-based customPackage and made the panel
  // and sticky footer read "$102.40 for 5 minutes" on a listing that is only sold by
  // the day (confirmed live: defaultPriceKey perDayPrice, selectedPackage
  // perMinutePrice, customPackage { minutes: 5 }).
  //
  // RAS-2520: modalityKey above is the draft's OWN package when it has one (a real
  // booking knows which tier it was made with), otherwise the listing's defaultPriceKey
  // - so this still decides the package type outright, just from the more specific of
  // the two authorities. Duration inference remains the fallback for drafts and listings
  // that declare neither. OrderPanel.js clamps the rendered form and the displayed
  // package for anything that still slips through.
  const inferredPackageType = daysMaybe >= 1 ? DAILY_PACKAGE_TYPE : HOURLY_PACKAGE_TYPE;
  const defaultPackageType = listingIsDayBased
    ? DAILY_PACKAGE_TYPE
    : listingIsTimeBased
    ? HOURLY_PACKAGE_TYPE
    : inferredPackageType;

  const listingPackageTypes = getListingPackageTypes(listing);
  if (!listingPackageTypes.includes(defaultPackageType)) {
    return null;
  }

  // Extract dates/times from draft
  let startDate, endDate, bookingStartTime, bookingEndTime;

  if (draft.bookingDates) {
    // Date-based format (from cart or rental defaults)
    const { startDate: sd, endDate: ed } = draft.bookingDates;
    startDate = sd instanceof Date ? sd : (sd ? new Date(sd) : null);
    endDate = ed instanceof Date ? ed : (ed ? new Date(ed) : null);
  } else if (draft.bookingStartTime && draft.bookingEndTime) {
    // Time-based format (from rental defaults)
    bookingStartTime = draft.bookingStartTime;
    bookingEndTime = draft.bookingEndTime;
    // Convert timestamps to dates for validation
    startDate = timestampToDate(bookingStartTime);
    endDate = timestampToDate(bookingEndTime);
  } else {
    return null; // No valid dates/times
  }


  // Validate dates are valid Date objects
  if (startDate && (isNaN(startDate.getTime()) || !(startDate instanceof Date))) {
    return null;
  }
  if (endDate && (isNaN(endDate.getTime()) || !(endDate instanceof Date))) {
    return null;
  }

  // If timeslots are not available yet, return null (will retry when available)
  if (!monthlyTimeSlots || Object.keys(monthlyTimeSlots).length === 0) {
    return null;
  }

  // Validate dates against timeslots
  const isValidDates = validateDatesAgainstTimeSlots(
    startDate,
    endDate,
    monthlyTimeSlots,
    timeZone,
    defaultPackageType
  );

  // Return null if dates are invalid OR if we cannot determine (null means uncertainty)
  if (isValidDates === false || isValidDates === null) {
    return null;
  }

  // Determine if this is a time-based or date-based booking
  const isTimeBased = defaultPackageType === HOURLY_PACKAGE_TYPE;
  const isDateBased = defaultPackageType === DAILY_PACKAGE_TYPE;

  // Build initial values object
  const initialValues = {};

  // Set seats/quantity with validation against max capacity
  const quantity = draft.quantity || draft.seats;
  const maxAvailableSeats = listing?.attributes?.publicData?.quantity;
  if (quantity) {
    let seats = Number(quantity);
    // Ensure seats >= 1
    if (seats < 1) {
      seats = 1;
    }
    // Cap seats to maxAvailableSeats if it exists
    if (maxAvailableSeats != null && seats > maxAvailableSeats) {
      seats = maxAvailableSeats;
    }
    initialValues.seats = seats;
  }

  // Set booking dates or times based on package type
  if (isTimeBased) {
    // For time-based bookings, use timestamps if available, otherwise extract from dates
    if (bookingStartTime && bookingEndTime) {
      initialValues.bookingStartTime = bookingStartTime;
      initialValues.bookingEndTime = bookingEndTime;
      // For FieldDateInput, we need date objects
      initialValues.bookingStartDate = { date: startDate };
      initialValues.bookingEndDate = { date: endDate };
    } else {
      // Extract timestamps from dates
      const { bookingStartTime: bst, bookingEndTime: bet } = extractTimeFromBookingDates(
        { startDate, endDate },
        timeZone
      );
      if (bst && bet) {
        initialValues.bookingStartTime = bst;
        initialValues.bookingEndTime = bet;
        initialValues.bookingStartDate = { date: startDate };
        initialValues.bookingEndDate = { date: endDate };
      }
    }
  } else if (isDateBased) {
    // For date-based bookings, use date range format
    initialValues.bookingDates = {
      startDate,
      endDate,
    };
  }

  // Validate fees against listing's additionalFees configuration
  const validatedFees = validateFeesAgainstListing(draft, listing);
  const listingRequiredFees = listing?.attributes?.publicData?.additionalFees?.filter(fee => fee.whenToApply === REQUIRED_UPFRONT)?.map(fee => fee.feeType) || [];

  // Set pickup/return options (self options are always allowed, even if fees don't exist)
  // Forms will default to self options if delivery/pickup services are not available
  if ((!!draft.pickupAtOwnerLocation || validatedFees.hasDeliveryFee === false) && !listingRequiredFees.includes(DELIVERY_FEE)) {
    initialValues.pickupAtOwnerLocation = [true];
  }

  if ((!!draft.returnAtOwnerLocation || validatedFees.hasPickupFee === false) && !listingRequiredFees.includes(PICKUP_FEE)) {
    initialValues.returnAtOwnerLocation = [true];
  }

  // Set usage multiple values (for usage-based fees) - only validated fees
  if (Object.keys(validatedFees.dynamicFees).length > 0) {
    Object.keys(validatedFees.dynamicFees).forEach(key => {
      initialValues[key] = validatedFees.dynamicFees[key];
    });
  }

  // Set other optional fees (array of selected fee types) - only validated fees
  if (validatedFees.otherOptionalFees.length > 0) {
    initialValues.otherOptionalFees = validatedFees.otherOptionalFees;
  }

  // Set required fees - only validated fees
  if (validatedFees.requiredFees.length > 0) {
    initialValues.requiredFees = validatedFees.requiredFees;
  }

  return {
    initialValues,
    isDailyPackage: isDateBased,
  };
};

/**
 * Compute initial values for booking forms from cart item
 * Uses shared computeInitialValuesFromDraft helper for DRY validation
 * @param {Object} cartItem - Cart item from Redux state
 * @param {Object} listing - Listing object
 * @param {Object} monthlyTimeSlots - Monthly timeslots structure
 * @param {String} timeZone - Timezone string
 * @returns {Object|null} Initial values object or null if invalid
 */
export const computeInitialValuesFromCartItem = (cartItem, listing, monthlyTimeSlots, timeZone) => {
  if (!cartItem || !listing) {
    return null;
  }

  // Validate that cart item belongs to this listing
  const listingId = listing.id?.uuid || listing.id;
  const cartListingId = cartItem.listingId || cartItem.listing?.id?.uuid || cartItem.listing?.id;

  if (listingId !== cartListingId) {
    return null;
  }

  // Use shared validation/compute function
  return computeInitialValuesFromDraft(cartItem, listing, monthlyTimeSlots, timeZone);
};

